import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getGmailCampaignDetails } from '../api';
import { DashboardPage, TableChip } from '../components/PremiumDesign';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { SearchInput, SkeletonText, CustomSelect } from '@/components';
import { V2Table } from '@/common/components';

const LOG_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
];

const decodeId = (encoded) => {
  if (!encoded) return '';
  try {
    return atob(encoded);
  } catch {
    return encoded;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

const renderLogStatusBadge = (status) => {
  let tone = 'neutral';
  let label = status;
  switch (String(status).toLowerCase()) {
    case 'sent':
    case 'accepted':
      tone = 'success';
      label = status === 'sent' ? 'Sent' : 'Accepted';
      break;
    case 'failed':
    case 'rejected':
      tone = 'error';
      label = 'Failed';
      break;
    case 'pending':
      tone = 'neutral';
      label = 'Pending';
      break;
    default:
      tone = 'neutral';
      label = status;
      break;
  }
  return (
    <TableChip
      tone={tone}
      className="!h-[26px] !px-2.5 !text-xs font-semibold !gap-1"
    >
      {label}
    </TableChip>
  );
};

export function EmailCampaignLogPage() {
  const { campaignId: rawCampaignId } = useParams();
  const campaignId = decodeId(rawCampaignId);
  const navigate = useNavigate();

  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const logsPerPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(logSearchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [logSearchQuery]);

  useEffect(() => {
    setCurrentLogPage(1);
  }, [debouncedSearchQuery, statusFilter]);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const {
    data: campaignData,
    isLoading: isDetailsApiLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'email-campaign-details-page',
      companyId,
      campaignId,
      debouncedSearchQuery,
    ],
    queryFn: () =>
      getGmailCampaignDetails({
        companyId,
        campaignId,
        params: debouncedSearchQuery ? { search: debouncedSearchQuery } : {},
      }),
    enabled: !!companyId && !!campaignId,
  });

  const isLoading = useDelayedSkeleton(isDetailsApiLoading);

  useEffect(() => {
    if (isError) {
      toast.error(
        error?.response?.data?.detail || 'Failed to load campaign details',
      );
    }
  }, [isError, error]);

  const rawLogs = campaignData?.logs || [];

  // Filter logs locally by status if set
  const filteredLogs = rawLogs.filter((log) => {
    if (
      statusFilter &&
      String(log.status).toLowerCase() !== statusFilter.toLowerCase()
    ) {
      return false;
    }
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      const matchName = (log.recipient_name || '')
        .toLowerCase()
        .includes(query);
      const matchEmail = (log.recipient_email || '')
        .toLowerCase()
        .includes(query);
      if (!matchName && !matchEmail) return false;
    }
    return true;
  });

  const startIndex = (currentLogPage - 1) * logsPerPage;
  const paginatedLogs = filteredLogs.slice(
    startIndex,
    startIndex + logsPerPage,
  );

  const logColumns = [
    {
      id: 'recipient_name',
      label: 'Recipient Name',
      defaultWidth: 200,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-28" height="h-4" />,
      cell: (log) => (
        <span
          className="truncate block font-medium text-[13px] sm:text-sm text-[#0F172A] whitespace-nowrap"
          title={log.recipient_name || ''}
        >
          {log.recipient_name || '—'}
        </span>
      ),
    },
    {
      id: 'recipient_email',
      label: 'Email Address',
      defaultWidth: 220,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-36" height="h-4" />,
      cell: (log) => (
        <span
          className="truncate block text-[13px] sm:text-sm text-[#1E293B] whitespace-nowrap"
          title={log.recipient_email || ''}
        >
          {log.recipient_email || '—'}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 130,
      minWidth: 100,
      resizable: true,
      align: 'center',
      skeleton: (
        <SkeletonText
          width="w-16"
          height="h-[26px]"
          className="mx-auto rounded-full"
        />
      ),
      cell: (log) => renderLogStatusBadge(log.status),
    },
    {
      id: 'failure_reason',
      label: 'Failure Reason',
      defaultWidth: 260,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-36" height="h-4" />,
      cell: (log) => (
        <span
          className="truncate block text-[13px] sm:text-sm text-[#475569] whitespace-nowrap"
          title={log.failure_reason || ''}
        >
          {log.failure_reason || '-'}
        </span>
      ),
    },
    {
      id: 'sent_time',
      label: 'Sent Time',
      defaultWidth: 190,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (log) => (
        <span className="truncate block text-[13px] sm:text-sm text-[#475569] whitespace-nowrap">
          {formatDate(log.sent_at || log.created_at)}
        </span>
      ),
    },
  ];

  return (
    <DashboardPage className="h-full flex-1 min-h-0 flex flex-col overflow-hidden !pt-2 sm:!pt-4">
      <div className="w-full flex flex-col gap-3 flex-1 min-h-0">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between flex-wrap gap-2 shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/email-campaigns')}
              className="w-9 h-9 rounded-lg border border-primary text-primary hover:bg-surface-mint flex items-center justify-center transition-colors focus:outline-none shrink-0"
              title="Back to Email Campaigns"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h3 className="text-h6 font-bold text-text-primary">
              {campaignData?.campaign_name
                ? campaignData.campaign_name
                : 'Recipient Delivery Logs'}
            </h3>
          </div>
          <span className="text-sm font-medium text-text-secondary flex items-center">
            {isLoading ? (
              <SkeletonText
                width="w-28"
                height="h-5"
                className="inline-block"
              />
            ) : (
              `Total Records: ${filteredLogs.length}`
            )}
          </span>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex items-center gap-3 w-full sm:max-w-xl flex-wrap sm:flex-nowrap shrink-0">
          <SearchInput
            value={logSearchQuery}
            onChange={(e) => setLogSearchQuery(e.target.value)}
            placeholder="Search logs by Email or Name"
            className="flex-1 min-w-[200px]"
          />
          <CustomSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={LOG_STATUS_OPTIONS}
            placeholder="All Statuses"
            className="w-full sm:w-[160px] h-[40px]"
            buttonClassName="h-10 border border-border-default bg-white rounded-xl w-full flex items-center justify-between px-4 text-sm font-medium text-text-primary focus:outline-none transition-all hover:border-border-active"
            textSizeClass="text-sm font-medium"
          />
        </div>

        {/* Table View */}
        <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col campaigns-table-compact [&_tbody_tr]:!h-[46px] [&_thead_tr]:!h-[42px] [&_td]:!py-2 [&_td]:!px-3 [&_th]:!py-2 [&_th]:!px-3">
          <V2Table
            tight
            columns={logColumns}
            data={paginatedLogs}
            rowKey="id"
            storageKey="email_campaign_logs_table_column_widths"
            loading={isLoading}
            loadingRowCount={5}
            empty={
              <div className="flex flex-col items-center justify-center gap-3">
                <span className="text-base font-medium">
                  No logs matched this criteria.
                </span>
              </div>
            }
            pagination={{
              currentPage: currentLogPage,
              totalCount: filteredLogs.length,
              itemsPerPage: logsPerPage,
              onChange: setCurrentLogPage,
            }}
          />
        </div>
      </div>
    </DashboardPage>
  );
}
